import { useCallback, useEffect, useState } from "react";
import AxiosInstance from "@/utils/AxiosInstance";
import type {
  CarePlanV2,
  DeviceV2,
  InternalNoteV2,
  NursingNoteV2,
  PatientProfileV2Data,
  PatientV2,
  TreatmentPlanV2,
} from "./types";

function safeArray<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value as T[]) : [];
}

export function usePatientProfileV2(
  patientId: string | undefined,
): PatientProfileV2Data {
  const [loading, setLoading] = useState(!!patientId);
  const [patient, setPatient] = useState<PatientV2 | null>(null);
  const [carePlans, setCarePlans] = useState<CarePlanV2[]>([]);
  const [treatmentPlans, setTreatmentPlans] = useState<TreatmentPlanV2[]>([]);
  const [devices, setDevices] = useState<DeviceV2[]>([]);
  const [nursingNotes, setNursingNotes] = useState<NursingNoteV2[]>([]);
  const [internalNotes, setInternalNotes] = useState<InternalNoteV2[]>([]);

  const refetch = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const [
        patientRes,
        carePlanRes,
        treatmentPlanRes,
        deviceRes,
        nursingRes,
        internalRes,
      ] = await Promise.all([
        AxiosInstance().get<{ data: PatientV2 }>(`/patients/${patientId}`),
        AxiosInstance().get<{ data: { carePlan: CarePlanV2[] } }>("/careplan", { params: { patientId } }),
        AxiosInstance().get<{ data: { treatmentPlan: TreatmentPlanV2[] } }>("/treatment-plan", { params: { patientId } }),
        AxiosInstance().get<{ data: { devices: DeviceV2[] } }>("/devices", { params: { patientId } }),
        AxiosInstance().get<{ data: { notes: NursingNoteV2[] } }>("/patients/nursing-note", { params: { patientId } }),
        AxiosInstance().get<{ data: { notes: InternalNoteV2[] } }>("/patients/internal-note", { params: { patientId } }),
      ]);
      setPatient(patientRes?.data?.data ?? null);
      setCarePlans(safeArray<CarePlanV2>(carePlanRes?.data?.data?.carePlan));
      setTreatmentPlans(
        safeArray<TreatmentPlanV2>(treatmentPlanRes?.data?.data?.treatmentPlan),
      );
      setDevices(safeArray<DeviceV2>(deviceRes?.data?.data?.devices));
      setNursingNotes(safeArray<NursingNoteV2>(nursingRes?.data?.data?.notes));
      setInternalNotes(safeArray<InternalNoteV2>(internalRes?.data?.data?.notes));
    } catch {
      setPatient(null);
      setCarePlans([]);
      setTreatmentPlans([]);
      setDevices([]);
      setNursingNotes([]);
      setInternalNotes([]);
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return {
    patient,
    carePlans,
    treatmentPlans,
    devices,
    nursingNotes,
    internalNotes,
    loading,
    refetch,
    setInternalNotes,
    setPatient,
  };
}
